<template>
  <div>
    <div class="row">
      <div class="col-xl-4 col-lg-6 position-static">
        <label class="col-form-label col-form-label-sm">
          {{ $t('Webportal.Category') }}
          <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Category.Description')">
            <font-awesome-icon :icon="['fas', 'info-circle']" />
          </span>
        </label>
        <b-input-group class="p-0 form-group position-static">
          <b-form-input size="sm" v-model="reportFileCategory" autocomplete="off" :disabled="disabled" :maxlength="30" />
          <template #append>
            <b-dropdown size="sm" id="reportfilecategory-dropdown" boundary="window" :disabled="disabled || reportFileCategoriesInternal.length == 0">
              <b-dropdown-item
                v-for="rft in reportFileCategoriesInternal"
                :key="rft.Name"
                @click="reportFileCategory = rft.Name">
                {{ rft.Name }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-input-group>
      </div>
      <div class="col-xl-8 col-lg-6">
        <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Location') }}</label>
        <custom-tree-select :placeholder="$t('Webportal.LocationFilter.Placeholder')" v-if="clientLocations.length > 0"
          ref="clientLocationTreeselect"
          class="form-group pr-2"
          instance-id="clientLocations"
          v-model="selectedClientLocations"
          :options="clientLocations"
          :multiple="true"
          value-format="object"
          :disabled="disabled"
          :limit="4"
          :limit-text="limitText"
          :append-to-body="true">
        </custom-tree-select>
        <div v-else-if="isBusy" class="form-group">
          <b-spinner small variant="primary"></b-spinner>
        </div>
        <div v-else class="form-group">
          {{ $t('Webportal.NoDataFound')}}
        </div>
      </div>
    </div>

    <div class="row">
      <label class="col-auto col-form-label col-form-label-sm">{{ $t('Webportal.Properties') }}</label>
      <b-form-radio-group class="col-auto form-group" size="sm"
          buttons button-variant="outline-primary"
          v-model="expandProperties">
        <b-form-radio :value="false">
          <font-awesome-icon fixed-width :icon="['far', 'rectangle-wide']" />
        </b-form-radio>
        <b-form-radio :value="true">
          <font-awesome-icon fixed-width :icon="['far', 'list-alt']" />
        </b-form-radio>
      </b-form-radio-group>
    </div>
    <custom-properties-input :disabled="disabled" v-model="propertyIds" :client-id="clientId" :only-visible="!userCanUseHiddenCustomProperties" :expand-properties="expandProperties" />
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import ReportFileMixin from '@/mixins/ReportFileMixin';
import CustomPropertiesInput from "@/components/Shared/CustomPropertiesInput";
import CustomTreeSelect from '@/components/Shared/CustomTreeSelect';
import AdministationService from '@/services/AdministationService';
import ReportFileService from '@/services/ReportFileService';

export default {
  name: "ReportFileProperties",

  mixins: [ AppConfig, ReportFileMixin ],

  components: { CustomPropertiesInput, CustomTreeSelect },

  props: {
    value: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    clientId: { type: Number, default: null },
    reportFiles: { type: Array, default: () => [] },
    reportFileCategories: { type: Array, default: () => [] },
    lockProperties: { type: Boolean, default: false },
  },

  data() {
    return {
      expandProperties: false,
      propertyIds: [],

      reportFileCategory: null,
      reportFileCategoriesCancelToken: null,
      reportFileCategoriesInternal: this.reportFileCategories,
      clientLocationsCancelToken: null,
      clientLocations: [],
      selectedClientLocations: [],

      isBusy: false,
    };
  },

  computed: {
    internalValue: function () {
      return {
        reportFileCategory: this.reportFileCategory,
        propertyIds: this.propertyIds,
        clientLocationIds: this.selectedClientLocations?.map(item => item.id)
      };
    },
  },

  watch: {
    internalValue(newVal, oldVal) {
      if (newVal != oldVal)
        this.$emit("input", newVal);
    },

    clientId() {
      this.clientLocations = [];
      this.selectedClientLocations = [];
      this.reportFileCategoriesInternal = [];
      this.reportFileCategory = null;

      this.getClientLocations();
      this.getReportFileCategories();
    },

    reportFiles(newVal, oldVal) {
      if (!this.lockProperties && !_.isEqual(newVal, oldVal)) {
        this.reportFileCategory = newVal.map(rf => rf.ReportFileCategory.Name)[0];

        this.propertyIds = _.uniq(newVal
          .filter(rf => rf.Properties)
          .map(rf => rf.Properties.map(p => p.PropertyValueIds).flat())
          .flat());

        this.selectedClientLocations = _.uniqBy(newVal
          .filter(rf => !_.isEmpty(rf.ClientLocations))
          .map(rf => rf.ClientLocations.map(this.getClientLocationItem))
          .flat(),
          item => item.id);
      }
    },

    reportFileCategories(newVal) {
      this.reportFileCategoriesInternal = newVal;
    },
  },

  methods: {
    getClientLocationItem(clientLocation) {
      return {
        id: clientLocation.ClientLocationId,
        label: [
          clientLocation.ClientLocationIdOnClientSite ?? '',
          clientLocation.Name ?? '',
          clientLocation.PostalCode ?? '',
          clientLocation.City ?? ''
        ].join(' ')
      };
    },

    getClientLocations() {
      if (this.clientId) {
        this.isBusy = true;

      // Cancel previous request
      if (this.clientLocationsCancelToken) {
        this.clientLocationsCancelToken.cancel();
      }

      this.clientLocationsCancelToken = axios.CancelToken.source();

      AdministationService.getClientLocations({ clientIds: [this.clientId] }, {}, this.clientLocationsCancelToken)
        .then(response => {
          this.clientLocations = response.data.items.map(item => this.getClientLocationItem(item.ClientLocation)) ?? [];
        })
        .finally(_ => {
          this.isBusy = false;
        });
      }
    },

    getReportFileCategories() {
      if (!this.clientId)
        return;

      // Cancel previous request
      if (this.reportFileCategoriesCancelToken)
        this.reportFileCategoriesCancelToken.cancel();

      this.reportFileCategoriesCancelToken = axios.CancelToken.source();

      ReportFileService.getReportFileCategories(this.clientId, this.reportFileCategoriesCancelToken)
        .then(response => {
          this.reportFileCategoriesInternal = response.data;
      });
    },
  },
};
</script>

<style>
  #reportfilecategory-dropdown .dropdown-menu {
      max-height: 400px;
      overflow-y: auto;
  }
</style>